import type { CreateOrganization } from '@stratumn/studio-shared';
import {
  CreateOrganizationSchema,
  OrganizationTypeSchema
} from '@stratumn/studio-shared';
import { useForm } from '@tanstack/react-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (dto: CreateOrganization) => Promise<boolean>;
};

const RequiredLabel = ({
  htmlFor,
  label
}: {
  htmlFor: string;
  label: string;
}) => (
  <div className="flex items-start gap-px">
    <Label htmlFor={htmlFor}>{label}</Label>
    <span aria-hidden className="text-destructive text-xs leading-none">
      *
    </span>
  </div>
);

export const CreateOrganizationDialog = ({
  open,
  onOpenChange,
  onCreate
}: Props) => {
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      organizationType: 'prod'
    } as CreateOrganization,
    validators: {
      onSubmit: ({ value }) => {
        const result = CreateOrganizationSchema.safeParse(value);
        if (result.success) {
          return;
        }
        return result.error.issues.map(issue => issue.message).join(', ');
      }
    },
    onSubmit: async ({ value }) => {
      const ok = await onCreate(CreateOrganizationSchema.parse(value));
      if (ok) {
        form.reset();
      }
    },
    onSubmitInvalid: ({ formApi }) => {
      const errors = formApi.state.errorMap.onSubmit;
      if (errors) {
        toast.error(
          typeof errors === 'string' ? errors : 'Invalid form values'
        );
      }
    }
  });

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            Add a new organization. Only non-prod organizations can later be
            linked as sub-organizations.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-4 px-6 py-6">
            <form.Field name="name">
              {field => (
                <div className="flex flex-col gap-2">
                  <RequiredLabel htmlFor="create-org-name" label="Name" />
                  <Input
                    id="create-org-name"
                    name={field.name}
                    onChange={e => field.handleChange(e.target.value)}
                    placeholder="Organization name"
                    type="text"
                    value={field.state.value}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="description">
              {field => (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="create-org-description">Description</Label>
                  <Textarea
                    id="create-org-description"
                    name={field.name}
                    onChange={e => field.handleChange(e.target.value)}
                    placeholder="Short description (optional)"
                    value={field.state.value ?? ''}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="organizationType">
              {field => (
                <div className="flex flex-col gap-2">
                  <Label>Type</Label>
                  <Select
                    onValueChange={value =>
                      field.handleChange(OrganizationTypeSchema.parse(value))
                    }
                    value={field.state.value}
                  >
                    <SelectTrigger aria-label="Organization type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OrganizationTypeSchema.options.map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          <DialogFooter>
            <DialogCancel onClick={() => onOpenChange(false)}>
              Cancel
            </DialogCancel>
            <form.Subscribe
              selector={state => [state.canSubmit, state.isSubmitting] as const}
            >
              {([canSubmit, isSubmitting]) => (
                <Button disabled={!canSubmit || isSubmitting} type="submit">
                  {isSubmitting ? 'Creating...' : 'Create'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
